<template>
  <div class="chart-div">
    <div class="chart-box">
      <div class="chart-title">
        <div class="chart-main-text">
          <span>{{ total }}</span>
        </div>
        <div
          class="chart-minor-text"
          @click="selectShow = !selectShow"
          ref="chartMinor"
        >
          <span>{{ this.targetName }}</span>
          <i class="iconfont icon-down"></i>
        </div>
      </div>
      <div ref="chart" class="chart-item"></div>
      <div class="chart-select" v-show="selectShow" ref="chartSelect">
        <ul>
          <li
            v-for="item in selectOption"
            :key="item.prop"
            :class="{ disable: target === item.prop }"
            @click="
              () => {
                changeSelect(item.prop);
              }
            "
          >
            {{ item.label }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import { getTimeToLA } from "@/utils/time.js";
import { valueFormat } from "@/utils/formatBigNum.js";

export default {
  props: {
    userData: {
      type: Object,
      default: () => {
        return {
          fb_ad_network_revenue: {
            date: [],
            value: [],
          },
          fb_ad_network_request: {
            date: [],
            value: [],
          },
          fb_ad_network_imp: {
            date: [],
            value: [],
          },
          fb_ad_network_impression_rate: {
            date: [],
            value: [],
          },
          fb_ad_network_click: {
            date: [],
            value: [],
          },
          fb_ad_network_ctr: {
            date: [],
            value: [],
          },
          fb_ad_network_cpm: {
            date: [],
            value: [],
          },
          fb_ad_network_fast_click_rate: {
            date: [],
            value: [],
          },
          fb_ad_network_fast_return_rate: {
            date: [],
            value: [],
          },
        };
      },
    },
    userTotalData: {
      type: Object,
      default: () => {
        return {
          fb_ad_network_revenue: 0,
          fb_ad_network_request: 0,
          fb_ad_network_imp: 0,
          fb_ad_network_impression_rate: 0,
          fb_ad_network_click: 0,
          fb_ad_network_ctr: 0,
          fb_ad_network_cpm: 0,
          fb_ad_network_fast_click_rate: 0,
          fb_ad_network_fast_return_rate: 0,
        };
      },
    },
    dataType: {
      type: String,
      default: "day",
    },
    defaultTarget: {
      type: String,
      default: "fb_ad_network_revenue",
    },
  },
  data() {
    return {
      selectShow: false,
      eCharts: null,
      target: "fb_ad_network_revenue",
      total: "",
      selectOption: [
        {
          label: "预估收入",
          prop: "fb_ad_network_revenue",
        },
        {
          label: "广告请求次数",
          prop: "fb_ad_network_request",
        },
        {
          label: "展示次数",
          prop: "fb_ad_network_imp",
        },
        {
          label: "展示率",
          prop: "fb_ad_network_impression_rate",
        },
        {
          label: "点击量",
          prop: "fb_ad_network_click",
        },
        {
          label: "点击率",
          prop: "fb_ad_network_ctr",
        },
        {
          label: "eCPM",
          prop: "fb_ad_network_cpm",
        },
        {
          label: "过早关闭率",
          prop: "fb_ad_network_fast_click_rate",
        },
        {
          label: "立即返回率",
          prop: "fb_ad_network_fast_return_rate",
        },
      ],
    };
  },
  beforeDestroy() {
    document.body.removeEventListener("click", this.handleClickOutside);
  },
  mounted() {
    this.target = this.defaultTarget;
    const dom = this.$refs.chart;
    this.eCharts = this.$echarts.init(dom);
    this.showTotalData();
    this.showData();
    document.body.addEventListener("click", this.handleClickOutside);
  },
  watch: {
    userData(n, o) {
      this.showData();
    },
    userTotalData(n, o) {
      this.showTotalData();
    },
  },
  computed: {
    targetName() {
      return this.selectOption.find((a) => a.prop === this.target).label;
    },
  },
  methods: {
    showData() {
      let that = this;
      const item = this.userData[this.target] ?? {
        date: [],
        value: [],
      };
      const showMarkLine =
        this.target === "fb_ad_network_fast_click_rate" ||
        this.target === "fb_ad_network_fast_return_rate";
      const option = {
        grid: {
          top: "30", // 调整图表上边距以留出更多空间给标题
          left: "12%",
          right: "10%",
          bottom: "15%",
        },
        tooltip: {
          trigger: "axis",
          padding: [10, 15],
          axisPointer: {
            type: "line",
          },
          formatter: function (params, ticket, callback) {
            var date = that.dateFormat(params[0].name);
            var seriesName = params[0].seriesName;
            var value = valueFormat(params[0].value, that.target);
            return (
              '<span style="display:block;margin-bottom:10px">' +
              date +
              "</span>" +
              '<span style="font-size:15px;font-weight: 600;">' +
              value +
              "</span>" +
              '<span style="font-size:15px;color:rgba(53,120,229,1)"> ' +
              seriesName +
              "</span>"
            );
          },
        },
        xAxis: {
          axisTick: {
            show: false,
          },
          axisPointer: {
            show: true,
          },
          type: "category",
          boundaryGap: false,
          data: item.date,
          axisLine: {
            show: true,
            lineStyle: {
              color: "rgba(238,238,238,1)",
            },
          },
          axisLabel: {
            color: "rgba(141,148,158,1)",
            formatter: function (value, index) {
              return that.dateFormat(value);
            },
          },
        },
        yAxis: {
          type: "value",
          min: function (value) {
            return parseInt(value.min);
          },
          axisLine: {
            show: true,
            lineStyle: {
              color: "rgba(238,238,238,1)",
            },
          },
          axisLabel: {
            color: "rgba(141,148,158,1)",
            formatter: function (value, index) {
              return valueFormat(value, that.target);
            },
          },
        },
        series: [
          {
            name: this.targetName,
            data: item.value,
            type: "line",
            itemStyle: {
              color: "rgba(53,120,229,1)",
            },
            lineStyle: {
              color: "rgba(53,120,229,1)",
              width: 2, // 折线宽度
            },
            markLine: {
              data: [
                {
                  name: "标记线",
                  yAxis:
                    this.target === "fb_ad_network_fast_click_rate"
                      ? 0.05
                      : 0.15,
                  lineStyle: {
                    color: "green",
                    opacity: showMarkLine ? 1 : 0,
                  },
                  label: {
                    show: true,
                    color: "green",
                    formatter: function (params) {
                      return "低";
                    },
                  },
                },
                {
                  name: "标记线",
                  yAxis:
                    this.target === "fb_ad_network_fast_click_rate"
                      ? 0.15
                      : 0.35,
                  lineStyle: {
                    color: "orange",
                    opacity: showMarkLine ? 1 : 0,
                  },
                  label: {
                    show: true,
                    color: "orange",
                    formatter: function (params) {
                      return "高";
                    },
                  },
                },
                {
                  name: "标记线",
                  yAxis:
                    this.target === "fb_ad_network_fast_click_rate"
                      ? 0.35
                      : 0.5,
                  lineStyle: {
                    color: "red",
                    opacity: showMarkLine ? 1 : 0,
                  },
                  label: {
                    show: true,
                    color: "red",
                    formatter: function (params) {
                      return "非常高";
                    },
                  },
                },
              ],
              silent: true,
            },
          },
        ],
      };
      if (this.eCharts) {
        this.eCharts.setOption(option);
        if (item.date.length == 0 || item.value.length == 0) {
          this.showDataEmpty();
          return;
        } else {
          this.eCharts.hideLoading();
        }
      }
    },
    showTotalData() {
      const data = this.userTotalData[this.target];
      const totalData = data ? data : 0;
      const userData = this.userData[this.target]?.value[0];
      if (this.dataType === "day" && totalData !== userData) {
        this.total = valueFormat(userData ? userData : 0, this.target);
      } else {
        this.total = valueFormat(totalData, this.target);
      }
    },
    showDataEmpty() {
      this.eCharts.showLoading({
        text: "暂无数据",
        showSpinner: false,
        textColor: "#9d9d9d",
        maskColor: "rgba(255, 255, 255, 0.8)",
        fontSize: "25px",
        fontWeight: "bold",
        fontFamily: "Microsoft YaHei",
      });
    },
    dateFormat(date) {
      const time = new Date(getTimeToLA(new Date(date).getTime()));
      if (this.dataType === "hour") {
        return `${time.getHours().toString().padStart(2, "0")}:00`;
      } else return `${time.getMonth() + 1}-${time.getDate()}`;
    },
    changeSelect(key) {
      this.target = key;
      this.selectShow = false;
      this.showData();
      this.showTotalData();
    },
    handleClickOutside(event) {
      if (this.$refs.chartMinor && this.$refs.chartMinor.contains(event.target))
        return;
      if (
        this.$refs.chartSelect &&
        !this.$refs.chartSelect.contains(event.target)
      ) {
        this.selectShow = false;
      }
    },
  },
};
</script>

<style lang="less" scoped>
.chart-div {
  width: calc(50% - 8px);
  float: left;
  margin: 8px 4px 0 4px;
  box-sizing: border-box;
}

.chart-box {
  position: relative;
  border-radius: 8px;
  background-color: #fff;
  box-shadow: 0ch 0px 5px rgba(0, 0, 0, 0.2);
}

.chart-item {
  height: 265px;
}

.chart-title {
  padding-top: 10px;
  padding-left: 15px;

  .chart-main-text {
    font-size: 30px;
    font-weight: bold;
    color: rgba(70, 70, 70, 1);
    margin-bottom: 5px;

    span {
      min-width: 30px;
      display: inline-block;
    }
  }

  .chart-minor-text {
    font-size: 18px;
    font-weight: 600;
    color: rgba(70, 70, 70, 1);

    .iconfont {
      margin-left: 5px;
      font-size: 15px;
    }
  }

  .chart-minor-text:hover {
    cursor: pointer;
  }
}

.chart-select {
  position: absolute;
  top: 85px;
  left: 15px;
  border-radius: 5px;
  background-color: #fff;
  box-shadow: 0ch 0px 5px rgba(0, 0, 0, 0.2);
  height: 250px;
  overflow: hidden;
  overflow-y: scroll;
  scrollbar-width: none;

  li {
    height: 18px;
    padding: 13px;
    text-align: center;
    line-height: 18px;
    color: rgba(141, 148, 158, 1);
    font-size: 15px;
  }

  li:hover {
    cursor: pointer;
  }

  li.disable {
    color: rgba(141, 148, 158, 0.5);
  }

  li:not(.disable):hover {
    background: rgba(242, 243, 245, 1);
  }
}

.chart-select::-webkit-scrollbar {
  display: none;
}
</style>
